import { useState, useEffect } from "react";
import { Form, useLoaderData, useActionData, useNavigation, useFetcher } from "react-router-dom";
import toast from "react-hot-toast";
import { Header } from "../../components/layout/Header";
import { Button, LinkButton } from "../../components/common";
import { useAuth } from "../../hooks/useAuth";
import type { OrganizationSettingsData } from "../../routes/loaders";
import type { ActionResult } from "../../routes/actions";
import { StripeConnectSection } from "../../components/organizations/StripeConnectSection";
import styles from "./OrganizationSettingsPage.module.css";

export default function OrganizationSettingsPage() {
  const { organization } = useLoaderData() as OrganizationSettingsData;
  const actionData = useActionData() as ActionResult | undefined;
  const navigation = useNavigation();
  const deleteFetcher = useFetcher();
  const { user, refetchUser } = useAuth();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isSubmitting = navigation.state === "submitting";
  const isDeleting = deleteFetcher.state === "submitting";

  const userOrg = user?.organizations.find((o) => o.id === organization.id);
  const userRole = userOrg?.role || null;

  useEffect(() => {
    if (!actionData) return;

    if (actionData.success) {
      toast.success("Organization updated successfully!");
      refetchUser();
    } else if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData, refetchUser]);

  useEffect(() => {
    if (deleteFetcher.data) {
      const result = deleteFetcher.data as ActionResult;
      if (result.error) {
        toast.error(result.error);
        setShowDeleteConfirm(false);
      }
    }
  }, [deleteFetcher.data]);

  return (
    <>
      <Header />
      <div className="container">
        <div className={styles.container}>
          <div className={styles.header}>
            <h1>Organization Settings</h1>
            <LinkButton to={`/organizations/${organization.id}`} variant="secondary">
              Back to Organization
            </LinkButton>
          </div>

          <div className={styles.section}>
            <h2>General Settings</h2>
            <Form method="post" className={styles.form}>
              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.name ? styles.formGroupError : ""}`}
              >
                <label htmlFor="name">Organization Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  defaultValue={organization.name}
                  placeholder="My Organization"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.name && (
                  <span className={styles.formError}>{actionData.fieldErrors.name}</span>
                )}
              </div>

              <div
                className={`${styles.formGroup} ${actionData?.fieldErrors?.slug ? styles.formGroupError : ""}`}
              >
                <label htmlFor="slug">URL Slug</label>
                <input
                  id="slug"
                  name="slug"
                  type="text"
                  defaultValue={organization.slug}
                  placeholder="my-organization"
                  disabled={isSubmitting}
                />
                {actionData?.fieldErrors?.slug && (
                  <span className={styles.formError}>{actionData.fieldErrors.slug}</span>
                )}
                <small className={styles.helpText}>
                  Only lowercase letters, numbers, and hyphens allowed.
                </small>
              </div>

              <div className={styles.formActions}>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  loadingText="Saving..."
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          </div>

          {(userRole === "owner" || userRole === "admin") && (
            <div className={styles.section}>
              <h2>API Keys</h2>
              <div className={styles.dangerContent}>
                <div>
                  <p>Manage API keys for programmatic access to your organization's resources.</p>
                </div>
                <LinkButton
                  to={`/organizations/${organization.id}/settings/api-keys`}
                  variant="primary"
                >
                  Manage API Keys
                </LinkButton>
              </div>
            </div>
          )}

          {userRole === "owner" && <StripeConnectSection organizationId={organization.id} />}

          {userRole === "owner" && (
            <div className={`${styles.section} ${styles.dangerZone}`}>
              <h2>Danger Zone</h2>
              <div className={styles.dangerContent}>
                <div>
                  <h3>Delete Organization</h3>
                  <p>
                    Once you delete an organization, there is no going back. This will permanently
                    delete the organization, all teams, and all memberships.
                  </p>
                </div>
                {!showDeleteConfirm ? (
                  <Button type="button" variant="danger" onClick={() => setShowDeleteConfirm(true)}>
                    Delete Organization
                  </Button>
                ) : (
                  <div className={styles.deleteConfirm}>
                    <p className={styles.confirmText}>
                      Are you sure? This action cannot be undone.
                    </p>
                    <div className={styles.confirmActions}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeleting}
                      >
                        Cancel
                      </Button>
                      <deleteFetcher.Form
                        method="post"
                        action={`/organizations/${organization.id}/settings/delete`}
                      >
                        <Button
                          type="submit"
                          variant="danger"
                          loading={isDeleting}
                          loadingText="Deleting..."
                        >
                          Yes, Delete Forever
                        </Button>
                      </deleteFetcher.Form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
